import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import SearchBar from '../components/SearchBar';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { searchProperties } from '../lib/api';
import { 
  Shield, 
  DollarSign, 
  HeartHandshake, 
  MapPin,
  ArrowRight,
  Loader2
} from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1753194931766-211c9f563156?w=1920&h=1080&fit=crop';

const CITIES = [
  { 
    id: 'sanibel', 
    name: 'Sanibel', 
    slug: 'sanibel',
    image: 'https://images.unsplash.com/photo-1585770609371-56545692c027?w=600&h=400&fit=crop'
  },
  { 
    id: 'naples', 
    name: 'Naples', 
    slug: 'naples',
    image: 'https://images.unsplash.com/photo-1673796751464-d4c1b3f86cf8?w=600&h=400&fit=crop'
  },
  { 
    id: 'fort-myers-beach', 
    name: 'Fort Myers Beach', 
    slug: 'fort-myers-beach',
    image: 'https://images.unsplash.com/photo-1726250947679-3d89963934d8?w=600&h=400&fit=crop'
  },
  { 
    id: 'cape-coral', 
    name: 'Cape Coral', 
    slug: 'cape-coral',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=600&h=400&fit=crop'
  },
  { 
    id: 'marco-island', 
    name: 'Marco Island', 
    slug: 'marco-island',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop'
  },
  { 
    id: 'captiva', 
    name: 'Captiva', 
    slug: 'captiva',
    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=600&h=400&fit=crop'
  },
];

const FEATURES = [
  {
    icon: DollarSign,
    title: 'Clear Pricing',
    description: 'No hidden fees or surprise charges. See exactly what you\'ll pay upfront.'
  },
  {
    icon: Shield,
    title: 'Trusted Properties',
    description: 'Every listing is verified and curated for quality Southwest Florida stays.'
  },
  {
    icon: HeartHandshake,
    title: 'Local Support',
    description: 'Our team knows SWFL inside and out. We\'re here to help every step of the way.'
  }
];

export default function HomePage() {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        const response = await searchProperties({ limit: 6 });
        setFeaturedProperties(response.data.items || []);
      } catch (error) {
        console.error('Failed to load featured properties:', error);
      } finally {
        setLoading(false);
      }
    };
    loadFeatured();
  }, []);

  return (
    <PublicLayout>
      <SEO
        title={null}
        description="Find vacation rentals in Cape Coral, Fort Myers, Sanibel, Naples & more. Browse curated Southwest Florida vacation homes with clear pricing and local support."
        url="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Luxury vacation rental in Southwest Florida"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        {/* Content */}
        <div className="container-main relative z-10 py-20">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
              Your Perfect Southwest Florida Getaway
            </h1>
            <p className="text-lg text-white/90 mb-8 animate-slide-up">
              Discover curated vacation rentals in Cape Coral, Fort Myers, Naples, and beyond. 
              Clear pricing. Local support. No surprises.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-4xl mt-8">
            <SearchBar variant="hero" />
          </div>
        </div>
      </section>

      {/* Why Book With Us */}
      <section className="section-padding bg-slate-50" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Book With Us
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Experience the difference of a platform built specifically for Southwest Florida vacationers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            {FEATURES.map((feature, index) => (
              <div key={index} className="feature-card text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="section-padding" data-testid="destinations-section">
        <div className="container-main">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Explore Southwest Florida
              </h2>
              <p className="text-slate-600">
                From pristine beaches to waterfront retreats, discover your ideal destination.
              </p>
            </div>
            <Link 
              to="/search" 
              className="hidden md:flex items-center gap-2 text-primary font-medium hover:underline"
            >
              View All Destinations
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {CITIES.map((city, index) => (
              <Link
                key={city.id}
                to={`/${city.slug}-vacation-rentals`}
                className={`
                  relative rounded-xl overflow-hidden group
                  ${index === 0 ? 'col-span-2 row-span-2' : ''}
                `}
                data-testid={`city-card-${city.id}`}
              >
                <div className={`aspect-property ${index === 0 ? 'md:aspect-square' : ''}`}>
                  <img 
                    src={city.image}
                    alt={city.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="flex items-center gap-1 text-white/80 text-sm mb-1">
                      <MapPin className="w-4 h-4" />
                      Southwest Florida
                    </div>
                    <h3 className="font-heading text-xl md:text-2xl font-semibold">
                      {city.name}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-6 text-center md:hidden">
            <Link to="/search">
              <Button className="btn-outline">
                View All Destinations
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="section-padding gradient-ocean" data-testid="featured-properties-section">
        <div className="container-main">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Featured Stays
              </h2>
              <p className="text-slate-600">
                Hand-picked properties for an unforgettable Florida vacation.
              </p>
            </div>
            <Link 
              to="/search" 
              className="hidden md:flex items-center gap-2 text-primary font-medium hover:underline"
            >
              Browse All Properties
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-slate-500">No properties available yet. Check back soon!</p>
            </div>
          )}

          <div className="mt-8 text-center">
            <Link to="/search">
              <Button className="btn-primary">
                View All Properties
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Host CTA */}
      <section className="section-padding bg-slate-900" data-testid="host-cta-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Own a Property in Southwest Florida?
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Join our community of hosts. Lower fees, local support, and guests who value quality stays.
            </p>
            <Link to="/host">
              <Button className="btn-accent">
                Learn About Hosting
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
